import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useChapterPages, useAllChapters } from '../api/useChapter'
import { useManga, useBookmark } from '../api/useManga'
import { useAuthStore } from '../store/authStore'
import client from '../api/client'
import PageScroller from '../components/reader/PageScroller'
import ReaderControls from '../components/reader/ReaderControls'
import ChapterCommentsDrawer from '../components/reader/ChapterCommentsDrawer'
import { Skeleton } from '../components/ui/Skeleton'
import NotFound from '../pages/NotFound'

export default function Reader() {
  const { slug, chapterNumber } = useParams()
  const navigate = useNavigate()
  const isAuth = useAuthStore((s) => s.isAuth)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [completionAction, setCompletionAction] = useState(null) // null | 'confirmed' | 'skipped'
  const lastScrollY = useRef(0)

  const chapterNum = parseInt(chapterNumber)
  const { data: pages, isLoading, isError } = useChapterPages(slug, chapterNum)
  const { data: allChapters } = useAllChapters(slug)
  const { data: manga } = useManga(slug)
  const { add: addBookmark, update: updateBookmark } = useBookmark(slug)

  const currentIndex = allChapters ? allChapters.findIndex((ch) => ch.number === chapterNum) : -1
  const currentChapter = currentIndex >= 0 ? allChapters[currentIndex] : null
  const prevChapter = currentIndex > 0 ? allChapters[currentIndex - 1] : null
  const nextChapter = allChapters && currentIndex < allChapters.length - 1 ? allChapters[currentIndex + 1] : null

  useEffect(() => {
    if (isAuth && currentChapter) {
      client.post('/api/user/history', {
        manga_id: currentChapter.manga_id,
        volume_id: currentChapter.volume_id,
        chapter_id: currentChapter.id,
      }).catch(() => {})
    }
  }, [slug, currentChapter?.id, isAuth])

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight
      setScrollProgress(maxScroll > 0 ? Math.min(100, (y / maxScroll) * 100) : 0)
      setShowScrollTop(y > 400)
      if (y > lastScrollY.current + 20) setControlsVisible(false)
      else if (y < lastScrollY.current - 20 || y < 80) setControlsVisible(true)
      lastScrollY.current = y
    }
    const handleMouseMove = (e) => { if (e.clientY < 60) setControlsVisible(true) }
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const handlePageTap = useCallback((e) => {
    const x = e.clientX, w = window.innerWidth
    if (x > w * 0.25 && x < w * 0.75) setControlsVisible((v) => !v)
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' && nextChapter) navigate(`/manga/${slug}/chapter/${nextChapter.number}`)
      else if (e.key === 'ArrowLeft' && prevChapter) navigate(`/manga/${slug}/chapter/${prevChapter.number}`)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [slug, prevChapter, nextChapter, navigate])

  if (isError) return <NotFound />

  const pageList = Array.isArray(pages) ? pages : pages?.pages ?? []

  return (
    <div className="min-h-screen" style={{ background: 'var(--reader-bg)' }} onClick={handlePageTap}>
      <ReaderControls
        visible={controlsVisible}
        mangaSlug={slug}
        mangaTitle={manga?.title || slug}
        chapterNumber={currentChapter?.number ?? chapterNumber}
        onCommentsToggle={() => setCommentsOpen((v) => !v)}
        commentsOpen={commentsOpen}
      />

      <ChapterCommentsDrawer
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        mangaSlug={slug}
        chapterId={currentChapter?.id}
        chapterNumber={currentChapter?.number ?? chapterNumber}
      />

      <div className="pt-12">
        {isLoading ? (
          <div className="flex flex-col items-center gap-2 py-8" style={{ maxWidth: 800, margin: '0 auto' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="w-full" style={{ height: 400 }} />
            ))}
          </div>
        ) : pageList.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-lg" style={{ color: 'var(--reader-muted)' }}>Страницы не найдены</p>
          </div>
        ) : (
          <PageScroller pages={pageList} chapterId={currentChapter?.id ?? chapterNumber} />
        )}
      </div>

      {!isLoading && pageList.length > 0 && (
        <div className="flex flex-col items-center gap-4 py-16" style={{ background: 'var(--reader-bg)' }}>
          <p className="text-sm" style={{ color: 'var(--reader-muted)' }}>Глава {currentChapter?.number} завершена</p>
          {nextChapter ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-base font-semibold" style={{ color: 'var(--reader-text)' }}>
                Следующая глава: {nextChapter.number}
              </p>
              <Link
                to={`/manga/${slug}/chapter/${nextChapter.number}`}
                className="px-8 py-3 rounded-xl font-bold transition-all hover:opacity-90 bg-accent text-on-accent"
              >
                Читать →
              </Link>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <p className="text-base" style={{ color: 'var(--reader-muted)' }}>Это последняя глава</p>
              <Link
                to={`/manga/${slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="reader-outline px-8 py-3 rounded-xl font-bold transition-all"
              >
                К тайтлу
              </Link>
              {isAuth && manga && manga.bookmark_status !== 'completed' && completionAction === null && (
                <div
                  className="mt-2 px-6 py-4 rounded-2xl flex flex-col items-center gap-3 text-center"
                  style={{ background: 'var(--reader-surface)', border: '1px solid var(--reader-border)', maxWidth: 320 }}
                >
                  <p className="text-sm font-semibold" style={{ color: 'var(--reader-text)' }}>
                    Вы дочитали мангу!
                  </p>
                  <p className="text-xs" style={{ color: 'var(--reader-muted)' }}>
                    Перенести в «Прочитано» в библиотеке?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const mutation = manga.is_bookmarked ? updateBookmark : addBookmark
                        mutation.mutate('completed', { onSuccess: () => setCompletionAction('confirmed') })
                      }}
                      disabled={addBookmark.isPending || updateBookmark.isPending}
                      className="px-5 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent disabled:opacity-60"
                    >
                      Да, перенести
                    </button>
                    <button
                      onClick={() => setCompletionAction('skipped')}
                      className="px-5 py-2 rounded-xl text-sm font-semibold transition-colors"
                      style={{ color: 'var(--reader-muted)' }}
                    >
                      Нет
                    </button>
                  </div>
                </div>
              )}
              {completionAction === 'confirmed' && (
                <p className="text-sm" style={{ color: 'var(--reader-muted)' }}>✓ Перенесено в «Прочитано»</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom navigation */}
      {!isLoading && pageList.length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', borderTop: '1px solid var(--reader-border)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-2">
            {prevChapter ? (
              <Link
                to={`/manga/${slug}/chapter/${prevChapter.number}`}
                className="reader-link flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
              >
                ← Гл. {prevChapter.number}
              </Link>
            ) : (
              <Link
                to={`/manga/${slug}`}
                onClick={() => window.scrollTo(0, 0)}
                className="reader-link flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
              >
                ← К тайтлу
              </Link>
            )}

            <span className="text-sm font-medium" style={{ color: 'var(--reader-muted)' }}>
              Гл. {currentChapter?.number ?? chapterNumber}
            </span>

            {nextChapter ? (
              <Link
                to={`/manga/${slug}/chapter/${nextChapter.number}`}
                className="reader-link flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm"
              >
                Гл. {nextChapter.number} →
              </Link>
            ) : (
              <span className="px-3 py-1.5 text-sm" style={{ color: 'var(--reader-muted)' }}>Конец</span>
            )}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="fixed bottom-10 left-0 right-0 z-50 h-1" style={{ background: 'var(--reader-border)' }}>
        <div
          className="h-full transition-all duration-150"
          style={{ width: `${scrollProgress}%`, background: 'var(--color-accent)' }}
        />
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <div className="fixed left-0 right-0 z-50 pointer-events-none" style={{ bottom: '4.5rem' }}>
          <div className="max-w-3xl mx-auto px-2 flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              className="reader-btn pointer-events-auto w-9 h-9 rounded-full flex items-center justify-center text-sm"
              title="Наверх"
            >
              ↑
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
